import { useState } from "react";
import { Box, Chip, Divider, FormControl, MenuItem, Select, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import type { FoodDictionaryDto } from "../../api/generated";
import { FOOD_GROUP, getLabel } from "../../enums/FoodDictionaryEnums";
import { RTL } from "../../cache/CacheRtl";
import { t } from "../dashboard/coach/coachTokens";
import { updateFoodDictionaryFoodGroup } from "../../services/system/FoodDictionaryService";
import { useMessageContext } from "../../contexts/MessageContext";

export type FoodGroupValue = NonNullable<FoodDictionaryDto["foodGroup"]>;

/**
 * The `<Select>` needs a string for every state, and "no group" is a state — MUI treats `null` as
 * uncontrolled. `""` is that state's spelling in the widget, and the only place it exists: it is
 * translated back to a real `null` before anything is sent.
 */
const UNSET = "";

type Props = {
  // The group is spelled `?: Group` on the generated DTO because springdoc does not mark nullable
  // fields, but the server really does send `null` for an unlabelled food — and that null is the
  // whole point of this control. Widen it here rather than pretend the field is merely absent.
  food: Pick<FoodDictionaryDto, "id"> & { foodGroup?: FoodGroupValue | null };
  /** Editing goes through a /system route, so only a system admin gets the control. */
  canEdit?: boolean;
  /** Re-read the food after a successful write, so the page and the server agree. */
  onUpdate?: () => void;
};

/**
 * The food's group — what it IS on a plate — shown in Hebrew, and correctable.
 *
 * <p><b>Why this is on the screen at all.</b> The group is chosen by an LLM labeller, not by a
 * person, and it is the ONLY thing the alternatives feature matches on: a trainee told to eat rice is
 * offered other starches and nothing else. A wrong group is therefore invisible until a trainee is
 * offered something absurd, and until now there was no way to see the model's answer, let alone say
 * it was wrong.
 *
 * <p><b>Why "clear" is an option and not an omission.</b> The labeller's queue is every food whose
 * group is null, so clearing puts the food back in it. That is the honest move when the model is
 * wrong and the person looking does not know the right answer either — better than filing the food
 * under a group somebody guessed, which is the rule the rest of the catalogue follows. It is listed
 * below a divider so it never reads as a thirteenth group.
 *
 * <p><b>Why the empty state gets a sentence.</b> A blank box here looks like a missing field. It is
 * actually the reason the food offers no swaps at all, which is worth saying out loud.
 */
const FoodGroupField = ({ food, canEdit = false, onUpdate }: Props) => {
  const notify = useMessageContext()?.notificationHandler;
  const [saving, setSaving] = useState(false);

  const current = food.foodGroup ?? null;

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const picked = event.target.value;
    // Back out of the widget's spelling: `""` is the clear action, everything else is a group name.
    const next = picked === UNSET ? null : (picked as FoodGroupValue);
    if (next === current) return;

    setSaving(true);
    try {
      await updateFoodDictionaryFoodGroup(food.id, next);
      notify?.success(
        next === null
          ? "הסיווג נוקה — המזון יסווג מחדש אוטומטית"
          : `קבוצת המזון עודכנה ל${getLabel(FOOD_GROUP, next)}`
      );
      onUpdate?.();
    } catch (error) {
      notify?.error("שגיאה בעדכון קבוצת המזון");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Typography sx={{ fontSize: "14px", color: "#64748B" }}>קבוצת מזון</Typography>

      {canEdit ? (
        <RTL>
          <FormControl size="small" sx={{ maxWidth: 260 }}>
            <Select
              value={current ?? UNSET}
              onChange={handleChange}
              disabled={saving}
              displayEmpty
              inputProps={{ "aria-label": "קבוצת מזון" }}
              renderValue={(value) =>
                value ? getLabel(FOOD_GROUP, value) : <Box component="span" sx={{ color: t.amberText }}>לא סווג עדיין</Box>
              }
              sx={{ fontSize: "14px", background: t.white, borderRadius: t.rxs }}
            >
              {FOOD_GROUP.map((group) => (
                <MenuItem key={group.value} value={group.value} sx={{ fontSize: "14px" }}>
                  {group.name}
                </MenuItem>
              ))}
              {/* Not a thirteenth group — the divider is what says so. */}
              <Divider />
              <MenuItem value={UNSET} sx={{ fontSize: "14px", color: t.amberText }}>
                ללא סיווג — החזרה לסיווג אוטומטי
              </MenuItem>
            </Select>
          </FormControl>
        </RTL>
      ) : (
        <Box>
          <Chip
            size="small"
            label={current ? getLabel(FOOD_GROUP, current) : "לא סווג עדיין"}
            sx={{
              fontSize: "12px",
              backgroundColor: current ? t.blueLight : t.amberLight,
              color: current ? t.blueText : t.amberText,
            }}
          />
        </Box>
      )}

      {/* Both of these are answers to "why does this food offer no swaps?", which is the question the
          group actually exists to settle. Neither is an error, so neither is styled as one. */}
      {current === null && (
        <Typography sx={{ fontSize: "12px", color: t.t2 }}>
          המזון עדיין לא סווג, ולכן לא מוצעות עבורו חלופות.
        </Typography>
      )}
      {current === "OTHER" && (
        <Typography sx={{ fontSize: "12px", color: t.t2 }}>
          מזון בקבוצה זו לא מוצע כחלופה ולא מקבל חלופות.
        </Typography>
      )}
    </Box>
  );
};

export default FoodGroupField;
